import { publicAuthRoutes } from "./publicAuthRoutes";
import React, { lazy } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  getFlatRoutes,
  getLegacyFlatRoutes,
  routeConfig,
  VIEW_MODE_MAP,
} from '@/config/routeConfig';
import RouteGuard from '@/components/RouteGuard/RouteGuard';
import H5DetailBackToList from '@/components/H5DetailBackToList';
import Profile from '@/containers/facility/Profile/Profile';
import {
  resolveLegacyContextRedirect,
} from '@/utils/route-context';
import {
  getDefaultFacilityPathFromState,
  getDefaultPortfolioPathFromState,
} from '@/hooks/use-route-context-sync';

const AccountSecurity = lazy(() => import("@/containers/common/AccountSecurity/AccountSecurity"));
const SwitchView = lazy(() => import('@/containers/LoginPages/SwitchView/SwitchView'));
const Register = lazy(() => import('@/containers/LoginPages/Register/Register'));
const SignUp = lazy(() => import('@/containers/LoginPages/SignUp/SignUp'));
const LayoutWrapper = lazy(() => import('@/layout'));
const NotFound = lazy(() => import('@/containers/LoginPages/404/NotFound'));
const Forbidden = lazy(() => import('@/containers/LoginPages/403/Forbidden'));
const ExternalServiceRequestDemo = lazy(() => import('@/containers/facility/ExternalServiceRequestDemo'));

const generateProtectedRoutes = () => {
  const allRoutes = [];

  Object.keys(routeConfig).forEach((viewType) => {
    const flatRoutes = getFlatRoutes(viewType);

    flatRoutes.forEach((route) => {
      allRoutes.push({
        path: route.path,
        element: (
          <RouteGuard requiredPermission={route.code} viewType={viewType}>
            <H5DetailBackToList route={route} viewType={viewType}>
              {route.component}
            </H5DetailBackToList>
          </RouteGuard>
        ),
        handle: {
          tab: route.title,
          routeCode: route.code,
          viewType,
          legacyPath: route.legacyPath,
        },
      });
    });
  });

  return allRoutes;
};

const LegacyContextRedirect = ({ viewType, route }) => {
  const location = useLocation();
  const state = useSelector((currentState) => currentState);
  const redirectPath = resolveLegacyContextRedirect(
    {
      pathname: location.pathname,
      search: location.search,
    },
    state
  );

  if (!redirectPath) {
    return <Navigate to="/switch-view" replace />;
  }

  return <Navigate to={redirectPath} replace />;
};

const generateLegacyRedirectRoutes = () => {
  const allRoutes = [];

  ["facility", "portfolio"].forEach((viewType) => {
    const flatRoutes = getLegacyFlatRoutes(viewType);

    flatRoutes.forEach((route) => {
      allRoutes.push({
        path: route.legacyPath,
        element: <LegacyContextRedirect viewType={viewType} route={route} />,
      });
    });
  });

  return allRoutes;
};

const DefaultRedirect = () => {
  const viewMode = useSelector((state) => state.App.viewMode);
  const state = useSelector((currentState) => currentState);
  const currentViewType = VIEW_MODE_MAP[viewMode];

  switch (currentViewType) {
    case 'facility': {
      const nextPath = getDefaultFacilityPathFromState(state);
      return <Navigate to={nextPath || '/switch-view'} replace />;
    }
    case 'admin':
      return <Navigate to="/admin/user-management" replace />;
    case 'portfolio': {
      const nextPath = getDefaultPortfolioPathFromState(state);
      return <Navigate to={nextPath || '/switch-view'} replace />;
    }
    case 'tennant':
      return <Navigate to="/tennant/service-request" replace />;
    default: {
      const facilityPath = getDefaultFacilityPathFromState(state);
      return <Navigate to={facilityPath || '/switch-view'} replace />;
    }
  }
};

export const routes = [
  ...publicAuthRoutes,
  { path: "/account/security", element: <AccountSecurity /> },
  {
    path: '/switch-view',
    element: <SwitchView />,
  },
  {
    path: '/register',
    element: <Register />,
  },
  {
    path: '/signup',
    element: <SignUp />,
  },
  {
    path: '/404',
    element: <NotFound />,
  },
  {
    path: '/403',
    element: <Forbidden />,
  },
  {
    path: '/service-request-demo',
    element: <ExternalServiceRequestDemo />,
  },
  {
    path: '/',
    element: <LayoutWrapper />,
    children: [
      {
        index: true,
        element: <DefaultRedirect />,
      },
      {
        path: '/common/profile',
        element: <Profile />,
        name: 'profile',
        handle: {
          tab: 'Profile',
        },
      },
      ...generateLegacyRedirectRoutes(),
      ...generateProtectedRoutes(),
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];
